import { isPetFriendlyPlant, type PlantSafety } from "@hexling/core";
import type { ContentCategory, ContentItem } from "@hexling/schema";
import { Chip, EmptyState, Spinner, TextField } from "@hexling/ui";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { PageTitle } from "../../components/Layout";
import { useContent } from "../../data/AppData";
import { categoryInfo } from "./categories";

interface Filter {
  id: string;
  label: string;
  test: (item: ContentItem) => boolean;
}

const safety = (i: ContentItem) => (i.data.safety ?? {}) as Record<string, unknown>;

/** Filters that make sense per category. Safety filters come first because they matter most. */
const FILTERS: Partial<Record<ContentCategory, Filter[]>> = {
  plant: [
    {
      id: "pet",
      label: "Pet friendly",
      test: (i) => isPetFriendlyPlant(safety(i) as unknown as PlantSafety),
    },
    { id: "culinary", label: "Kitchen safe", test: (i) => safety(i).ingestion === "culinary" },
  ],
  recipe: [
    { id: "pet", label: "Pet friendly", test: (i) => safety(i).pet_friendly === true },
    { id: "edible", label: "Edible", test: (i) => safety(i).ingestible === true },
  ],
  crystal: [
    {
      id: "water",
      label: "Water safe",
      test: (i) => (i.data.care as { water_safe?: boolean } | undefined)?.water_safe === true,
    },
  ],
  oil: [{ id: "carrier", label: "Carrier oils", test: (i) => i.data.oil_type === "carrier" }],
  tarot_card: [
    { id: "major", label: "Major arcana", test: (i) => i.data.arcana === "major" },
    { id: "minor", label: "Minor arcana", test: (i) => i.data.arcana === "minor" },
  ],
};

/** One short line under each name, specific to the category. */
function subtitle(item: ContentItem): string {
  const d = item.data;
  if (typeof d.latin_name === "string" && d.latin_name) return d.latin_name;
  if (item.category === "recipe") return String(d.type ?? "").replace(/_/g, " ");
  if (Array.isArray(d.intents))
    return (d.intents as string[]).slice(0, 3).join(", ").replace(/_/g, " ");
  return item.summary;
}

export function CategoryPage({ category }: { category: ContentCategory }) {
  const content = useContent();
  const info = categoryInfo(category);
  const [q, setQ] = useState("");
  const [active, setActive] = useState<string[]>([]);

  if (content.isPending) return <Spinner />;
  const index = content.data;
  if (!index || !info)
    return (
      <EmptyState title="This shelf doesn't exist">Head back to the library to browse.</EmptyState>
    );

  const filters = FILTERS[category] ?? [];
  const base = q.trim() ? index.search(q, [category], 500) : index.list(category);
  const items = base.filter((i) => active.every((id) => filters.find((f) => f.id === id)!.test(i)));
  const toggle = (id: string) =>
    setActive((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  return (
    <>
      <PageTitle
        title={info.label}
        subtitle={info.blurb}
        back={{ to: "/library", label: "Library" }}
      />
      <TextField
        label={`Search ${info.label.toLowerCase()}`}
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />
      {filters.length > 0 && (
        <div className="chips" role="group" aria-label="Filters">
          {filters.map((f) => (
            <Chip key={f.id} pressed={active.includes(f.id)} onToggle={() => toggle(f.id)}>
              {f.label}
            </Chip>
          ))}
        </div>
      )}
      <p className="hx-muted" role="status">
        {items.length} {items.length === 1 ? info.singular : `${info.singular}s`}
      </p>
      {items.length ? (
        <ul className="label-list">
          {items.map((item) => (
            <li key={item.slug}>
              <Link
                className="label-row"
                to="/library/$category/$slug"
                params={{ category, slug: item.slug }}
              >
                <span>
                  <span className="label-row__name">{item.name}</span>
                  <span className="label-row__blurb">{subtitle(item)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState glyph="🔍" title="Nothing matches">
          Clear a filter or try another word.
        </EmptyState>
      )}
    </>
  );
}
